import React, { useMemo } from 'react';
import { Calendar, ChevronRight, AlertTriangle, Clock, CheckCircle2, Target } from 'lucide-react';
import type { LookAheadReport, LookAheadTask } from '../../types/schedule';

interface LookAheadViewProps {
  report?: LookAheadReport;
  isLoading?: boolean;
  onTaskClick?: (task: LookAheadTask) => void;
  weeks?: 2 | 4;
}

interface WeekSectionProps {
  weekNumber: number;
  startDate: Date;
  endDate: Date;
  tasks: LookAheadTask[];
  onTaskClick?: (task: LookAheadTask) => void;
}

const statusIcons: Record<string, React.ReactNode> = {
  not_started: <Clock className="h-4 w-4 text-gray-400" />,
  in_progress: <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />,
  completed: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  on_hold: <Clock className="h-4 w-4 text-yellow-500" />,
  cancelled: <div className="h-2 w-2 rounded-full bg-gray-400" />,
};

const priorityColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const WeekSection: React.FC<WeekSectionProps> = ({
  weekNumber,
  startDate,
  endDate,
  tasks,
  onTaskClick,
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isCurrentWeek = weekNumber === 1;

  return (
    <div
      className={`border rounded-lg ${isCurrentWeek ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`}
    >
      {/* Week Header */}
      <div
        className={`px-4 py-3 border-b ${isCurrentWeek ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calendar className={`h-4 w-4 ${isCurrentWeek ? 'text-red-500' : 'text-gray-500'}`} />
            <span className={`font-semibold ${isCurrentWeek ? 'text-red-900' : 'text-gray-900'}`}>
              Week {weekNumber}
            </span>
            {isCurrentWeek && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                Current
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500">
            {formatDate(startDate)} - {formatDate(endDate)}
          </span>
        </div>
      </div>

      {/* Tasks */}
      <div className="divide-y divide-gray-100">
        {tasks.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">
            <Target className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No tasks scheduled for this week</p>
          </div>
        ) : (
          tasks.map(task => (
            <div
              key={task._id}
              onClick={() => onTaskClick?.(task)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-start space-x-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-1">{statusIcons[task.status]}</div>

                {/* Task Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-mono text-gray-500">{task.wbsCode}</span>
                    {task.isCritical && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Critical
                      </span>
                    )}
                    {task.taskType === 'milestone' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                        <Target className="h-3 w-3 mr-1" />
                        Milestone
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">{task.name}</p>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500">
                      {new Date(task.plannedStart).toLocaleDateString()} -{' '}
                      {new Date(task.plannedEnd).toLocaleDateString()}
                    </span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${priorityColors[task.priority]}`}
                    >
                      {task.priority}
                    </span>
                  </div>
                </div>

                {/* Progress */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-medium text-gray-900">{task.percentComplete}%</div>
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                    <div
                      className="h-1.5 bg-red-500 rounded-full"
                      style={{ width: `${task.percentComplete}%` }}
                    />
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const LookAheadView: React.FC<LookAheadViewProps> = ({
  report,
  isLoading = false,
  onTaskClick,
  weeks = 2,
}) => {
  // Group tasks by week
  const weeklyTasks = useMemo(() => {
    if (!report?.tasks) return [];

    const now = new Date();
    const weekGroups: Array<{
      weekNumber: number;
      startDate: Date;
      endDate: Date;
      tasks: LookAheadTask[];
    }> = [];

    for (let i = 0; i < weeks; i++) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + i * 7 - now.getDay()); // Start of week (Sunday)
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const tasksInWeek = report.tasks.filter(task => {
        const taskStart = new Date(task.plannedStart);
        const taskEnd = new Date(task.plannedEnd);
        // Task overlaps with this week
        return taskStart <= weekEnd && taskEnd >= weekStart;
      });

      weekGroups.push({
        weekNumber: i + 1,
        startDate: weekStart,
        endDate: weekEnd,
        tasks: tasksInWeek,
      });
    }

    return weekGroups;
  }, [report?.tasks, weeks]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2].map(i => (
          <div key={i} className="border border-gray-200 rounded-lg animate-pulse">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="h-5 bg-gray-200 rounded w-32" />
            </div>
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Summary Header */}
      {report?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs font-medium text-gray-500 uppercase">Total Tasks</div>
            <div className="text-xl font-semibold text-gray-900">{report.summary.totalTasks}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-xs font-medium text-green-600 uppercase">Starting This Period</div>
            <div className="text-xl font-semibold text-green-900">
              {report.summary.startingThisPeriod}
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3">
            <div className="text-xs font-medium text-yellow-600 uppercase">Ending This Period</div>
            <div className="text-xl font-semibold text-yellow-900">
              {report.summary.endingThisPeriod}
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <div className="text-xs font-medium text-red-600 uppercase">Critical</div>
            <div className="text-xl font-semibold text-red-900">{report.summary.criticalTasks}</div>
          </div>
        </div>
      )}

      {/* Week Sections */}
      {weeklyTasks.map(week => (
        <WeekSection
          key={week.weekNumber}
          weekNumber={week.weekNumber}
          startDate={week.startDate}
          endDate={week.endDate}
          tasks={week.tasks}
          onTaskClick={onTaskClick}
        />
      ))}

      {/* Empty State */}
      {weeklyTasks.every(w => w.tasks.length === 0) && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No tasks in look-ahead period</h3>
          <p className="text-sm text-gray-500">
            Tasks scheduled for the next {weeks} weeks will appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default LookAheadView;
